import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import FunnelLayout from "@/v2/components/FunnelLayout";
import GlassCard from "@/v2/components/GlassCard";
import PrimaryCTA from "@/v2/components/PrimaryCTA";
import PaymentTestModeBanner from "@/v2/components/checkout/PaymentTestModeBanner";
import StripeEmbeddedCheckout from "@/v2/components/checkout/StripeEmbeddedCheckout";
import AuthDialog from "@/v2/components/AuthDialog";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment, hasPaymentsConfigured } from "@/lib/stripe";
import { loadResult } from "@/v2/lib/realityCheckApi";
import { toast } from "@/hooks/use-toast";

type Tier = "self_service" | "with_call";

const selfFeatures = [
  "5 step adattati alla tua diagnosi RC",
  "Template e guida per ogni fase del test",
  "Dashboard per tracciare le tue evidenze",
  "Accesso immediato dopo il pagamento",
];
const callFeatures = [
  "Tutto del piano Self-Service",
  "Call 1:1 sulle evidenze raccolte (45 min)",
  "Si sblocca al completamento dello Step 2",
  "Feedback diretto su cosa sta funzionando e cosa no",
  "Decisione go/no-go supportata da un advisor",
  "Accesso immediato dopo il pagamento",
];

function FeatureList({ items }: { items: string[] }) {
  return (
    <ul className="text-sm text-slate-300 mt-5 space-y-2.5 flex-1">
      {items.map((f) => (
        <li key={f} className="flex items-start gap-2">
          <Check className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busyTier, setBusyTier] = useState<Tier | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingTier, setPendingTier] = useState<Tier | null>(null);

  async function startCheckout(tier: Tier) {
    if (!user) {
      setPendingTier(tier);
      setAuthOpen(true);
      return;
    }
    if (!hasPaymentsConfigured()) {
      toast({
        title: "Pagamenti non disponibili",
        description: "Configurazione Stripe mancante.",
        variant: "destructive",
      });
      return;
    }
    setBusyTier(tier);
    try {
      const realityCheckId = loadResult()?.id;
      const returnUrl = `${window.location.origin}/v2/checkout/success?session_id={CHECKOUT_SESSION_ID}`;
      const { data, error } = await supabase.functions.invoke("v2-create-checkout", {
        body: {
          tier,
          environment: getStripeEnvironment(),
          returnUrl,
          realityCheckId,
        },
      });
      if (error) throw error;
      if (data?.type === "already_active") {
        navigate(data.redirect ?? "/v2/sprint");
        return;
      }
      if (data?.type === "unauthorized") {
        setPendingTier(tier);
        setAuthOpen(true);
        return;
      }
      if (data?.type === "checkout" && data.clientSecret) {
        setClientSecret(data.clientSecret);
        return;
      }
      throw new Error("Unexpected response");
    } catch (e) {
      console.error(e);
      toast({
        title: "Errore",
        description: "Non siamo riusciti ad aprire il pagamento. Riprova.",
        variant: "destructive",
      });
    } finally {
      setBusyTier(null);
    }
  }

  function closeCheckout() {
    setClientSecret(null);
  }

  return (
    <FunnelLayout>
      <Helmet>
        <title>Prezzi | Proof | Progetto Startup</title>
        <meta name="description" content="Scegli il tuo percorso su Proof. Self-Service €49 o Sprint + Call €129. Rimborso garantito se non sei soddisfatto." />
        <link rel="canonical" href="https://progettostartup.com/v2/pricing" />
        <meta property="og:title" content="Prezzi | Proof | Progetto Startup" />
        <meta property="og:description" content="Scegli il tuo percorso su Proof. Self-Service €49 o Sprint + Call €129. Rimborso garantito se non sei soddisfatto." />
        <meta property="og:url" content="https://progettostartup.com/v2/pricing" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Prezzi | Proof | Progetto Startup" />
        <meta name="twitter:description" content="Scegli il tuo percorso su Proof. Self-Service €49 o Sprint + Call €129. Rimborso garantito se non sei soddisfatto." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Proof — Validation Sprint",
          description: "Percorso guidato di 7 giorni per testare un'idea imprenditoriale con dati reali.",
          brand: { "@type": "Brand", name: "Progetto Startup" },
          url: "https://progettostartup.com/v2/pricing",
          offers: [
            {
              "@type": "Offer",
              name: "Self-Service",
              price: "49",
              priceCurrency: "EUR",
              availability: "https://schema.org/InStock",
            },
            {
              "@type": "Offer",
              name: "Sprint + Call",
              price: "129",
              priceCurrency: "EUR",
              availability: "https://schema.org/InStock",
            },
          ],
        })}</script>

      </Helmet>
      <PaymentTestModeBanner />

      <div className="max-w-4xl mx-auto space-y-10">
        <header className="text-center space-y-3">
          <p className="text-[11px] uppercase tracking-[0.2em] text-secondary font-medium">
            Pricing
          </p>
          <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
            Scegli il tuo percorso Proof
          </h1>
          <p className="text-slate-300 max-w-xl mx-auto">
            7 giorni, protocollo guidato, evidenze concrete.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6 items-stretch md:pt-6">
          <GlassCard className="flex flex-col">
            <h2 className="font-display font-semibold text-lg">Sprint Self-Service</h2>
            <p className="text-xs text-slate-400 mt-1">Per chi vuole muoversi da solo</p>
            <p className="font-display text-4xl font-bold mt-4">
              49<span className="text-2xl text-slate-400">€</span>
            </p>
            <FeatureList items={selfFeatures} />
            <button
              type="button"
              onClick={() => startCheckout("self_service")}
              disabled={busyTier !== null}
              className="mt-6 w-full inline-flex items-center justify-center rounded-full border border-white/20 bg-white/5 hover:bg-white/10 px-6 py-3 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busyTier === "self_service" ? "Apertura…" : "Scegli Self-Service"}
            </button>
            <p className="mt-3 text-center text-xs text-slate-500">
              Non sei soddisfatto entro 48 ore dall'acquisto? Ti rimborsiamo.
            </p>
          </GlassCard>

          <div className="relative md:-mt-3">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 text-[11px] tracking-wider bg-gradient-to-r from-[#7DD3E8] to-[#A8E4F0] text-slate-900 px-3 py-1 rounded-full font-bold uppercase shadow-lg">
              Consigliato
            </span>
            <GlassCard
              className={[
                "flex flex-col h-full",
                "bg-gradient-to-b from-white/[0.08] to-white/[0.04]",
                "border-[#7DD3E8]/40",
                "shadow-[0_0_0_1px_rgba(125,211,232,0.25),0_25px_70px_-25px_rgba(125,211,232,0.35)]",
              ].join(" ")}
            >
              <h2 className="font-display font-semibold text-lg">Sprint + Call</h2>
              <p className="text-xs text-slate-400 mt-1">Per chi vuole un confronto reale</p>
              <p className="font-display text-4xl font-bold mt-4">
                129<span className="text-2xl text-slate-400">€</span>
              </p>
              <FeatureList items={callFeatures} />
              <button
                type="button"
                onClick={() => startCheckout("with_call")}
                disabled={busyTier !== null}
                className="mt-6 w-full inline-flex items-center justify-center rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busyTier === "with_call" ? "Apertura…" : "Scegli con Call"}
              </button>
              <p className="mt-3 text-center text-xs text-slate-500">
                Non sei soddisfatto entro 48 ore dall'acquisto? Ti rimborsiamo.
              </p>
            </GlassCard>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500">
          Pagamento sicuro tramite Stripe.
        </p>
      </div>

      <AuthDialog
        open={authOpen}
        onOpenChange={(open) => {
          setAuthOpen(open);
          if (!open) setPendingTier(null);
        }}
        defaultTab="signup"
        onSuccess={() => {
          setAuthOpen(false);
          const tier = pendingTier;
          setPendingTier(null);
          if (tier) void startCheckout(tier);
        }}
      />

      {clientSecret && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm overflow-y-auto"
          role="dialog"
          aria-modal="true"
        >
          <div className="min-h-full flex items-start md:items-center justify-center p-3 md:p-6">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
                <p className="text-sm font-semibold text-slate-900">Completa il pagamento</p>
                <button
                  type="button"
                  onClick={closeCheckout}
                  className="text-slate-500 hover:text-slate-900 transition"
                  aria-label="Chiudi"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="bg-white">
                <StripeEmbeddedCheckout clientSecret={clientSecret} />
              </div>
            </div>
          </div>
        </div>
      )}
    </FunnelLayout>
  );
}
